import styled from 'styled-components'

import { FormattedSlot } from '@/apiTypes.ts'
import { DoctorImage, DoctorInitials } from '@/components/Doctor.tsx'
import { Text } from '@/components/Text.tsx'
import { SortedSlotGroup, useAppointmentStore } from '@/store.ts'

const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
  gap: 24px;
`

const Day = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  border: ${({ theme }) => theme.colors.common.border} 1px solid;
  border-radius: 2px;
`

const Date = styled.div`
  height: 46px;
  display: flex;
  align-items: center;
  border-bottom: ${({ theme }) => theme.colors.common.border} 1px solid;
  padding: 0 16px;
  width: 100%;
`

const Strong = styled(Text)`
  color: #222222;
  font-weight: 500;
`

const TimeSlot = styled.button`
  display: flex;
  flex-flow: row;
  align-items: center;
  padding: 13px 16px 16px 16px;
  gap: 24px;
  border-bottom: 1px solid rgba(34, 34, 34, 0.1);
  width: 100%;
  outline: none;

  &:last-child {
    border-bottom: none;
  }
`

const TimeSlotDetail = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
`

const TimeSlotTime = styled.div<{ $isSelected: boolean }>`
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.common.accentMedium : theme.colors.common.white};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.common.white : theme.colors.common.black95};
  border: 1px solid
    ${({ $isSelected, theme }) =>
      $isSelected
        ? theme.colors.common.accentMedium
        : theme.colors.common.border};
  border-radius: 100px;
  height: 32px;
  width: 69px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
`

interface TimeSlotsProps {
  slots: SortedSlotGroup[]
  onSlotClick?: (slot: FormattedSlot) => void
}

const TimeSlots = ({ onSlotClick, slots }: TimeSlotsProps) => {
  const setSelectedSlot = useAppointmentStore((state) => state.setSelectedSlot)
  const selectedSlot = useAppointmentStore((state) => state.selectedSlot)

  const handleSlotClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    slot: FormattedSlot,
  ) => {
    e.preventDefault()
    setSelectedSlot(slot)
    onSlotClick?.(slot)
  }

  return (
    <Wrapper>
      {slots.map((day, index) => (
        <Day key={index}>
          <Date>
            <Strong>{day.date}</Strong>
          </Date>
          {day.slots.map((slot) => (
            <TimeSlot
              key={slot.id}
              id="timeSlot"
              aria-label={slot.start.format('dddd, DD/MM/YYYY - HH:mm')}
              onClick={(e) => handleSlotClick(e, slot)}
            >
              {slot.practitioner.picture ? (
                <DoctorImage
                  src={slot.practitioner.picture}
                  alt={slot.practitioner.name}
                />
              ) : (
                <DoctorInitials name={slot.practitioner.name} />
              )}
              <TimeSlotDetail>
                <Strong>{slot.practitioner.name}</Strong>
                <Text>{slot.location.name || slot.location.fullName}</Text>
              </TimeSlotDetail>
              <TimeSlotTime $isSelected={selectedSlot?.id === slot.id}>
                {slot.start.format('HH:mm')}
              </TimeSlotTime>
            </TimeSlot>
          ))}
        </Day>
      ))}
    </Wrapper>
  )
}

export { TimeSlots }
